import { useLocalStorage } from "@scribenote/utils";
import { useCallback, useEffect, useEffectEvent } from "react";

import { useAuth } from "./use-auth";

const STORAGE_KEY_PREFIX = "broadcastNotificationState";

interface BroadcastState {
  readAt?: string;
  dismissedAt?: string;
}

type BroadcastStateMap = Record<string, BroadcastState>;

interface BroadcastLike {
  uuid: string;
  isBroadcast?: boolean | null;
}

interface Options {
  // Pass the latest notifications list to drop localStorage entries
  // for broadcasts that no longer exist server-side. Omit if the
  // caller only sees a filtered subset (e.g. unreadOnly).
  pruneAgainst?: readonly (BroadcastLike | null)[] | null;
}

export function useBroadcastNotificationState(opts: Options = {}) {
  const { userUuid } = useAuth();
  const storageKey = userUuid
    ? `${STORAGE_KEY_PREFIX}:${userUuid}`
    : `${STORAGE_KEY_PREFIX}:anonymous`;
  const [broadcastState, setBroadcastState] =
    useLocalStorage<BroadcastStateMap>(storageKey, {});

  const markBroadcastRead = useCallback(
    (uuid: string) => {
      setBroadcastState((prev) => ({
        ...prev,
        [uuid]: { ...prev[uuid], readAt: new Date().toISOString() },
      }));
    },
    [setBroadcastState],
  );

  const dismissBroadcast = useCallback(
    (uuid: string) => {
      setBroadcastState((prev) => ({
        ...prev,
        [uuid]: {
          ...prev[uuid],
          dismissedAt: new Date().toISOString(),
        },
      }));
    },
    [setBroadcastState],
  );

  const pruneAgainst = opts.pruneAgainst;

  const runPrune = useEffectEvent((knownUuids: string[]) => {
    const keep = new Set(knownUuids);
    const next: BroadcastStateMap = {};
    let changed = false;
    for (const uuid of Object.keys(broadcastState)) {
      if (keep.has(uuid)) {
        next[uuid] = broadcastState[uuid];
      } else {
        changed = true;
      }
    }
    if (!changed) return;
    setBroadcastState(next);
  });

  useEffect(() => {
    if (!pruneAgainst) return;
    const uuids = pruneAgainst
      .filter((n): n is BroadcastLike => Boolean(n?.isBroadcast))
      .map((n) => n.uuid);
    runPrune(uuids);
  }, [pruneAgainst]);

  return { broadcastState, markBroadcastRead, dismissBroadcast };
}

export function applyBroadcastState<
  T extends {
    uuid: string;
    isBroadcast: boolean;
    readAt?: unknown;
    dismissedAt?: unknown;
  },
>(
  notifications: (T | null)[] | null | undefined,
  state: BroadcastStateMap,
): T[] {
  return (notifications ?? []).filter(Boolean).map((n) => {
    if (!n!.isBroadcast) return n!;
    const local = state[n!.uuid];
    if (!local) return n!;
    return {
      ...n!,
      readAt: local.readAt ?? n!.readAt,
      dismissedAt: local.dismissedAt ?? n!.dismissedAt,
    };
  }) as T[];
}
